import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { fmtMoney0, fmtDate, statutLabel, statutColor, clotureDepassee, DEVIS_STATUTS } from "../lib/format";
import { Badge, Spinner, EmptyState, Confirm } from "../components/ui";

export default function DevisList() {
  const [devis, setDevis] = useState<any[] | null>(null);
  const [filters, setFilters] = useState<any>({});
  const [del, setDel] = useState<any>(null);
  const nav = useNavigate();

  const load = () => api.get("/devis").then((r) => setDevis(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!devis) return [];
    return devis.filter(
      (d) =>
        (!filters.q ||
          (d.objet || "").toLowerCase().includes(filters.q.toLowerCase()) ||
          String(d.numero).includes(filters.q) ||
          (d.client_nom || "").toLowerCase().includes(filters.q.toLowerCase())) &&
        (!filters.statut || d.statut === filters.statut) &&
        (!filters.annee || (d.date_devis || "").startsWith(filters.annee))
    );
  }, [devis, filters]);

  if (!devis) return <Spinner />;
  const annees = Array.from(new Set(devis.map((d) => (d.date_devis || "").slice(0, 4)).filter(Boolean))).sort().reverse();

  const duplicate = async (d: any) => {
    try {
      const { data } = await api.post(`/devis/${d.id}/duplicate`);
      nav(`/devis/${data.id}`);
    } catch (e) {
      alert(apiError(e));
    }
  };

  return (
    <div data-testid="devis-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Devis</h1>
        <Link data-testid="devis-new-btn" className="nx-btn-primary" to="/devis/nouveau">Nouveau devis</Link>
      </div>
      <div className="nx-card overflow-x-auto">
        <div className="flex flex-wrap gap-2 border-b border-line p-3">
          <input data-testid="devis-filter-q" className="nx-input max-w-[220px]" placeholder="N°, objet, client…" value={filters.q || ""} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          <select data-testid="devis-filter-statut" className="nx-input max-w-[180px]" value={filters.statut || ""} onChange={(e) => setFilters({ ...filters, statut: e.target.value })}>
            <option value="">Tous statuts</option>
            {DEVIS_STATUTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className="nx-input max-w-[120px]" value={filters.annee || ""} onChange={(e) => setFilters({ ...filters, annee: e.target.value })}>
            <option value="">Toutes années</option>
            {annees.map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
        {filtered.length === 0 ? <EmptyState message="Aucun devis" /> : (
          <table className="nx-table">
            <thead>
              <tr><th>N°</th><th>Objet</th><th>Client</th><th>Revendeur</th><th>Produit</th><th>Entité</th><th className="text-right">Montant HT</th><th>Statut</th><th>Prob.</th><th>Date</th><th className="whitespace-nowrap">Clôture est.</th><th>Prop.</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`devis-row-${d.numero}`}>
                  <td><Link className="font-extrabold text-green underline-offset-2 hover:underline" to={`/devis/${d.id}`}>{d.numero}</Link></td>
                  <td className="max-w-[240px] truncate">{d.objet}</td>
                  <td>{d.client_nom}</td>
                  <td>{d.revendeur_nom}</td>
                  <td>{[d.produit_nom, d.sous_produit_nom].filter(Boolean).join(" / ")}</td>
                  <td>{d.entite_code && <Badge className="bg-green text-white">{d.entite_code}</Badge>}</td>
                  <td className="text-right font-semibold">{fmtMoney0(d.total_ht, d.devise)}</td>
                  <td><Badge className={statutColor(d.statut)}>{statutLabel(d.statut)}</Badge></td>
                  <td>{d.probabilite} %</td>
                  <td>{fmtDate(d.date_devis)}</td>
                  <td data-testid={`devis-cloture-${d.numero}`} className={clotureDepassee(d) ? "font-semibold text-amber" : ""}>{d.date_cloture_estimee ? fmtDate(d.date_cloture_estimee) : ""}</td>
                  <td>{d.owner_initiales}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`devis-duplicate-btn-${d.numero}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => duplicate(d)}>Dupliquer</button>
                      <button data-testid={`devis-delete-btn-${d.numero}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDel(d)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {del && (
        <Confirm
          message={`Supprimer (archiver) le devis N° ${del.numero} ?`}
          onConfirm={async () => {
            await api.delete(`/devis/${del.id}`);
            setDel(null);
            load();
          }}
          onCancel={() => setDel(null)}
        />
      )}
    </div>
  );
}
